/**
 * LanguageMenu — the single, shared language switcher for the whole portal.
 *
 * One component, used everywhere a language can be picked (TopBar header + the
 * login screen) so the trigger and the dropdown never drift apart again. It
 * always shows the **current** language (globe + code + chevron) and opens a
 * menu of all four languages (native names from `LANG_LABELS`, check on the
 * active one) — the pattern the header already shipped.
 *
 * Two visual `variant`s:
 *   • `bar`  — themed chrome for the TopBar (Tamagui tokens; follows light/dark
 *              + accent).
 *   • `navy` — fixed navy/bronze chrome for the login screen (literal hexes,
 *              NOT accent-driven, mirroring the login lockup).
 *
 * Open state is **uncontrolled by default** (login just drops it in). The
 * TopBar passes `open` / `onOpenChange` so it can keep the language, notifications
 * and profile menus mutually exclusive.
 */
import { useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, LANGS, LANG_LABELS } from '../i18n'
import { palette } from '../theme/palette'
import { Backdrop } from './Backdrop'
import { Icon } from './Icon'
import { AnimatePresence } from './motion'

type Variant = 'bar' | 'navy'

interface LanguageMenuProps {
  variant?: Variant
  /** Horizontal anchor of the dropdown relative to the trigger. */
  align?: 'left' | 'right'
  /** Controlled open state — omit for self-managed (uncontrolled). */
  open?: boolean
  /** Notified on every open/close (also fires in controlled mode). */
  onOpenChange?: (open: boolean) => void
}

// Fixed navy/bronze chrome for the login screen (mirrors app/login.tsx LOGIN).
const NAVY = {
  triggerBorder: 'rgba(255,255,255,0.22)',
  triggerBg: 'rgba(255,255,255,0.06)',
  triggerHover: 'rgba(255,255,255,0.12)',
  text: '#DBE3F7',
  icon: '#A9B8DA',
  menuBg: palette.coverNavy, // #16265C
  menuBorder: 'rgba(255,255,255,0.16)',
  rowActiveBg: 'rgba(255,255,255,0.10)',
  rowHover: 'rgba(255,255,255,0.06)',
  rowText: '#DBE3F7',
  rowActiveText: '#FFFFFF',
  accent: '#ECD6A2', // brushed-bronze highlight (active code + check)
} as const

export function LanguageMenu({ variant = 'bar', align = 'right', open: openProp, onOpenChange }: LanguageMenuProps) {
  const { l, lang, setLang } = useApp()
  const [openSelf, setOpenSelf] = useState(false)
  const controlled = openProp !== undefined
  const open = controlled ? !!openProp : openSelf

  const setOpen = (next: boolean) => {
    if (!controlled) setOpenSelf(next)
    onOpenChange?.(next)
  }

  const navy = variant === 'navy'

  return (
    <Stack position="relative">
      {/* trigger — always shows the CURRENT language */}
      <XStack
        onPress={() => setOpen(!open)}
        cursor="pointer"
        animation="quick"
        height={34}
        alignItems="center"
        gap={navy ? 6 : 5}
        paddingHorizontal={navy ? 13 : 10}
        borderRadius={navy ? 999 : 7}
        borderWidth={1}
        borderColor={navy ? NAVY.triggerBorder : open ? '$primary' : '$borderColor'}
        backgroundColor={navy ? NAVY.triggerBg : 'transparent'}
        hoverStyle={{ backgroundColor: navy ? NAVY.triggerHover : '$backgroundHover' }}
        pressStyle={navy ? { opacity: 0.85 } : undefined}
        role="button"
        accessibilityLabel={l('set.language')}
      >
        <Icon name="globe" size={15} color={navy ? NAVY.icon : '$muted'} />
        <Text fontSize={12} fontWeight="700" letterSpacing={navy ? 0.6 : 0} color={navy ? NAVY.text : '$muted'}>
          {lang.toUpperCase()}
        </Text>
        <Icon name="chevron-down" size={14} color={navy ? NAVY.icon : '$muted'} />
      </XStack>

      <AnimatePresence>
        {open && (
          <>
            <Backdrop onPress={() => setOpen(false)} />
            <YStack
              animation="quicker"
              enterStyle={{ opacity: 0, y: -6, scale: 0.97 }}
              exitStyle={{ opacity: 0, y: -6, scale: 0.97 }}
              opacity={1}
              y={0}
              scale={1}
              position="absolute"
              top={42}
            {...(align === 'left' ? { left: 0 } : { right: 0 })}
            width={180}
            zIndex={50}
            padding={6}
            borderWidth={1}
            borderRadius={navy ? 12 : 7}
            backgroundColor={navy ? NAVY.menuBg : '$bgElevated'}
            borderColor={navy ? NAVY.menuBorder : '$borderSecondary'}
            shadowColor={palette.shadow}
            shadowOpacity={navy ? 0.5 : 0.18}
            shadowRadius={navy ? 28 : 20}
            shadowOffset={{ width: 0, height: 14 }}
          >
            {LANGS.map((code) => {
              const on = code === lang
              return (
                <XStack
                  key={code}
                  onPress={() => {
                    setLang(code)
                    setOpen(false)
                  }}
                  cursor="pointer"
                  alignItems="center"
                  gap={10}
                  paddingHorizontal={12}
                  paddingVertical={8}
                  borderRadius={navy ? 8 : 5}
                  backgroundColor={navy ? (on ? NAVY.rowActiveBg : 'transparent') : on ? '$primaryBg' : 'transparent'}
                  hoverStyle={{
                    backgroundColor: navy ? (on ? NAVY.rowActiveBg : NAVY.rowHover) : on ? '$primaryBg' : '$backgroundHover',
                  }}
                  role="button"
                  aria-selected={on}
                >
                  <Text
                    width={24}
                    fontSize={11}
                    fontWeight="700"
                    color={navy ? (on ? NAVY.accent : NAVY.icon) : on ? '$primary' : '$muted'}
                  >
                    {code.toUpperCase()}
                  </Text>
                  <Text
                    flex={1}
                    fontSize={13}
                    fontWeight={on ? '600' : '400'}
                    color={navy ? (on ? NAVY.rowActiveText : NAVY.rowText) : on ? '$primary' : '$color'}
                  >
                    {LANG_LABELS[code]}
                  </Text>
                  {on && <Icon name="check" size={15} color={navy ? NAVY.accent : '$primary'} />}
                </XStack>
              )
            })}
            </YStack>
          </>
        )}
      </AnimatePresence>
    </Stack>
  )
}
